import { Button, Group, Tooltip, useMatches } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { MarketCarTableRow } from "../MarketCarTableRow/MarketCarTableRow";
import { ModelComparator } from "../ModelComparator";

import { EmptyState, ListTable, Modal, useColumnSorter } from "@/components/elements";
import PopoverTable from "@/components/elements/PopoverTable/PopoverTable";
import { IconTags } from "@/components/icons";
import { usePlansUser } from "@/services/plans/get-user-plan";
import { useAddCarToWatchlist } from "@/services/watchlists/add-car-to-watchlist";
import { useWatchlists } from "@/services/watchlists/get-watchlists";
import { useRemoveCarFromWatchlist } from "@/services/watchlists/remove-car-from-watchlist";

import type {
  ListTableColumnSortBy,
  ListTableComparisonProps,
  ListTableTh,
} from "@/components/elements";
import type { MarketCar, MarketCarColumns } from "@/types/market";
import type { MantineStyleProps } from "@mantine/core";
import type { FC } from "react";

export type MarketCarsTableProps = {
  columns?: MarketCarColumns[];
  columnsDisabled?: MarketCarColumns[];
  decade?: string;
  getPath?: (carId: string) => string;
  hideComparison?: boolean;
  makeId?: string;
  marketCars: MarketCar[];
  namespace?: string;
  onModelsCompare?: (model1Id: string, model2Id: string) => void;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  onSearch?: (query: string) => void;
  pending?: boolean;
  setMarketCarsState: React.Dispatch<React.SetStateAction<MarketCar[]>>;
  page?: number;
  pageSize?: number;
  totalCars?: number;
} & MantineStyleProps;

export const MarketCarsTable: FC<MarketCarsTableProps> = ({
  columns = [],
  columnsDisabled,
  decade,
  getPath,
  hideComparison = false,
  makeId,
  marketCars,
  namespace = "market-cars-table",
  onModelsCompare,
  onPageChange,
  onPageSizeChange,
  onSearch,
  pending = false,
  setMarketCarsState,
  page = 1,
  pageSize = 25,
  totalCars = 0,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: watchlists } = useWatchlists();
  const { mutate: addCarToWatchlistMutate, isPending: isPendingAddCarToWatchlist } =
    useAddCarToWatchlist();
  const { mutate: removeCarFromWatchlistMutate, isPending: isPendingRemoveCarFromWatchlist } =
    useRemoveCarFromWatchlist();

  const [popoverOpened, setPopoverOpened] = useState(false);
  const [opened, { open, close }] = useDisclosure();
  const isMobile = useMatches({ base: true, sm: false });
  const hasComparison = !hideComparison && !!onModelsCompare;

  const { data: plans } = usePlansUser();
  const userPlan = plans?.name;
  const canCompareByPlan = userPlan === "collector" || userPlan === "professional";

  const { getSortById, getUpdateSort } = useColumnSorter<MarketCar, MarketCarColumns>(
    `${namespace}-sort`
  );

  function handleChangeSortBy(id: MarketCarColumns) {
    return (sortBy?: ListTableColumnSortBy) => {
      getUpdateSort(id)(sortBy);
    };
  }

  function getColumnSorter(id: MarketCarColumns) {
    return (
      <ListTable.ColumnSorter
        tooltip={t("market.table.sort_tooltip")}
        sortBy={getSortById(id)}
        onSortChange={handleChangeSortBy(id)}
      />
    );
  }

  const tableColumns: ListTableTh[] = columns.map((id) => ({
    id,
    label: t(`market.table.cols.${id}`, id),
    sorter: getColumnSorter(id),
  }));

  const hasPageSizeChanger = !!onPageSizeChange && totalCars > 10;
  const hasPagination = !!onPageChange && totalCars > pageSize;

  const actionInPending = isPendingAddCarToWatchlist || isPendingRemoveCarFromWatchlist || pending;

  const addCarToWatchlistHandler = (car: MarketCar, watchlistIds: string[]) => {
    setMarketCarsState((prev) =>
      prev.map((c) =>
        c.id === car.id
          ? {
              ...c,
              in_wishlists: Array.from(new Set([...(c.in_wishlists ?? []), ...watchlistIds])),
            }
          : c
      )
    );
    watchlistIds.forEach((wid) => addCarToWatchlistMutate({ carId: car.id, watchlistId: wid }));
  };

  function navigateToCar(carId: string) {
    if (getPath) {
      const path = getPath(carId);
      navigate(path);
    }
  }

  const removeCarFromWatchlistHandler = (car: MarketCar, watchlistId: string) => {
    setMarketCarsState((prev) =>
      prev.map((c) =>
        c.id === car.id
          ? { ...c, in_wishlists: (c.in_wishlists ?? []).filter((id) => id !== watchlistId) }
          : c
      )
    );
    removeCarFromWatchlistMutate({ carId: car.id, watchlistId });
  };

  function handleModelsCompare(model1Id: string, model2Id: string) {
    onModelsCompare?.(model1Id, model2Id);
    close();
  }

  return (
    <ListTable.Context columns={tableColumns} disabled={columnsDisabled} namespace={namespace}>
      {({ filteredColumns, headingColumns, onFilteredColumnsChange }) => {
        const columnsId = headingColumns.map(({ id }) => id) as MarketCarColumns[];

        return (
          <ListTable.SelectionContext data={marketCars.map((car) => car.id)} maxSelection={2}>
            {({ checkStatus, getRowCheckbox, isSelected, onToggleAll, reset, selected }) => {
              const comparisonProps = hasComparison
                ? ({
                    checkStatus,
                    hasComparison: true,
                    onToggleAll,
                  } as ListTableComparisonProps)
                : {};

              return (
                <>
                  <Group gap="md" p="lg">
                    {onSearch && (
                      <ListTable.SearchInput
                        onChange={onSearch}
                        placeholder={t("market.table.search_placeholder")}
                        resetTooltip={t("market.table.search_reset_tooltip")}
                        updateOnChange
                      />
                    )}
                    {isMobile ? (
                      <PopoverTable
                        popoverOpened={popoverOpened}
                        setPopoverOpened={() => setPopoverOpened((prev) => !prev)}
                        actionInPending={false}
                        filteredColumns={filteredColumns}
                        onFilteredColumnsChange={onFilteredColumnsChange}
                      />
                    ) : (
                      <ListTable.ColumnsOrganizer
                        columns={filteredColumns}
                        label={t("common.customizeColumns")}
                        onChange={onFilteredColumnsChange}
                        tooltip={t("common.organizeColumns")}
                      />
                    )}
                    {hasComparison && (
                      <>
                        <Tooltip
                          label={t("plans.contentLocker.upgradeToAccess")}
                          disabled={canCompareByPlan}
                          withArrow
                          events={{ hover: true, focus: true, touch: true }}
                          position="top"
                          offset={6}
                          maw={300}
                          openDelay={100}
                          multiline
                        >
                          <Button
                            display={isMobile ? "none" : "inherit"}
                            onClick={open}
                            leftSection={<IconTags />}
                            disabled={marketCars.length < 2 || !canCompareByPlan}
                            aria-label={t("market.table.compare")}
                          >
                            {t("market.table.compare")}
                          </Button>
                        </Tooltip>
                        <Tooltip
                          label={t("plans.contentLocker.upgradeToAccess")}
                          disabled={canCompareByPlan}
                          withArrow
                          events={{ hover: true, focus: true, touch: true }}
                          position="top"
                          offset={-8}
                          maw={300}
                          openDelay={100}
                          multiline
                        >
                          <Button
                            px="xs"
                            onClick={open}
                            display={!isMobile ? "none" : "inherit"}
                            disabled={marketCars.length < 2 || !canCompareByPlan}
                            aria-label={t("market.table.compare")}
                          >
                            <IconTags />
                          </Button>
                        </Tooltip>
                      </>
                    )}
                  </Group>
                  {marketCars.length > 0 ? (
                    <>
                      <ListTable
                        data={{
                          head: [
                            {
                              id: "wishlist-col",
                              children: <></>,
                              thProps: { style: { width: 1 } },
                            },
                            ...headingColumns,
                          ],
                          body: marketCars.map((car) => (
                            <MarketCarTableRow
                              key={car.id}
                              checkbox={hasComparison ? getRowCheckbox(car.id) : undefined}
                              columns={columnsId}
                              data={car}
                              setCarToAdd={() => {}}
                              addCarToWatchlistHandler={(watchlistIds) =>
                                addCarToWatchlistHandler(car, watchlistIds)
                              }
                              removeCarFromWatchlistHandler={(watchlistId) =>
                                removeCarFromWatchlistHandler(car, watchlistId)
                              }
                              watchlists={watchlists}
                              actionInPending={actionInPending}
                              isWished={(car.in_wishlists?.length ?? 0) > 0}
                              onClick={() => navigateToCar(car.id)}
                              selected={hasComparison ? isSelected(car.id) : false}
                              to={getPath?.(car.id)}
                              style={{
                                whiteSpace: "nowrap",
                              }}
                            />
                          )),
                        }}
                        tableProps={{ highlightOnHover: true }}
                        {...comparisonProps}
                      />
                      {(hasPageSizeChanger || hasPagination) && (
                        <Group justify="flex-end" p="lg">
                          {hasPageSizeChanger && (
                            <ListTable.PageSizeChanger
                              pageSize={pageSize}
                              onChange={onPageSizeChange}
                              label={t("components.ListTable.page_size_changer_label")}
                            />
                          )}
                          {hasPagination && (
                            <ListTable.Pagination
                              divider={t("components.ListTable.pagination_divider")}
                              page={page}
                              pageSize={pageSize}
                              onChange={onPageChange}
                              total={totalCars}
                            />
                          )}
                        </Group>
                      )}
                    </>
                  ) : (
                    <EmptyState type="noResults" text={t("garage.no_results")} />
                  )}

                  {hasComparison && (
                    <Modal
                      opened={opened}
                      onClose={close}
                      centered
                      icon={IconTags}
                      title={t("market.table.compare_modal")}
                    >
                      <ModelComparator
                        defaultModel1Id={selected[0]}
                        defaultModel2Id={selected[1]}
                        decade={decade}
                        makeId={makeId}
                        onCancel={close}
                        onCompare={(model1Id, model2Id) => {
                          handleModelsCompare(model1Id, model2Id);
                          reset();
                        }}
                      />
                    </Modal>
                  )}
                </>
              );
            }}
          </ListTable.SelectionContext>
        );
      }}
    </ListTable.Context>
  );
};
